import { ReactNode, useEffect, useRef } from "react";
import { VISUALLY_HIDDEN } from "../visuallyHidden";
import { KebabMenu, MenuItemDef } from "../KebabMenu";
import { Checkbox } from "../Choice";
import { LoadingSkeleton } from "../States";
import { usePhone } from "../usePhone";
import s from "./DataTable.module.scss";

export type Column<Row> = { id: string; header: string; cell: (row: Row) => ReactNode; phonePrimary?: boolean; hideOnPhone?: boolean };

export type RowKey = string | number;
export type TableSelection<Row> = {
  selected: Set<RowKey>; onToggle: (key: RowKey) => void; onToggleAll: () => void;
  allLabel: string; noneLabel: string; rowLabel: (row: Row) => string;
};

type Props<Row> = {
  caption: string; columns: Column<Row>[]; rows: Row[]; rowKey: (row: Row) => string | number;
  rowActions?: (row: Row) => MenuItemDef[]; /** The ⋯ button's name for a row, e.g. "More for Home visits". */ rowActionsLabel?: (row: Row) => string; onRowClick?: (row: Row) => void;
  /** Adds a checkbox column (card header on phones). Absent: the table behaves exactly as before. */
  selection?: TableSelection<Row>;
  /** Shows skeleton rows with the same number of columns instead of the data. */
  loading?: boolean; loadingRows?: number;
};

/** A table on desktop; a list of cards on phones (DESIGN_SYSTEM: important lists become cards). */
export function DataTable<Row>({ caption, columns, rows, rowKey, rowActions, rowActionsLabel, onRowClick, selection, loading, loadingRows = 5 }: Props<Row>) {
  const phone = usePhone();
  const headRef = useRef<HTMLInputElement>(null);
  const picked = selection ? rows.filter((r) => selection.selected.has(rowKey(r))).length : 0;
  const allPicked = rows.length > 0 && picked === rows.length;
  const somePicked = picked > 0 && !allPicked;
  useEffect(() => { if (headRef.current) headRef.current.indeterminate = somePicked; }, [somePicked, phone, loading]);
  if (loading) {
    const cols = phone ? 2 : columns.length + (selection ? 1 : 0) + (rowActions ? 1 : 0);
    return <LoadingSkeleton variant="rows" rows={loadingRows} columns={cols} label={caption} />;
  }
  const rowBox = (row: Row) => selection && (
    <span className={s.cardCheck} onClick={(e) => e.stopPropagation()}>
      <Checkbox label={null} aria-label={selection.rowLabel(row)} checked={selection.selected.has(rowKey(row))} onChange={() => selection.onToggle(rowKey(row))} />
    </span>
  );
  if (phone) {
    const primary = columns.find((c) => c.phonePrimary) ?? columns[0];
    const rest = columns.filter((c) => c !== primary && !c.hideOnPhone);
    return (
      <ul aria-label={caption} className={s.cards}>
        {rows.map((row) => (
          <li key={rowKey(row)} data-selected={selection?.selected.has(rowKey(row)) || undefined}
            className={[s.card, onRowClick ? s.clickable : "", selection?.selected.has(rowKey(row)) ? s.selectedCard : ""].join(" ").trim()} onClick={onRowClick ? () => onRowClick(row) : undefined}>
            <div className={s.cardHead}>
              <div className={s.cardLead}>
                {rowBox(row)}
                {onRowClick
                  ? <button type="button" className={s.cardTitle} onClick={(e) => { e.stopPropagation(); onRowClick(row); }}>{primary.cell(row)}</button>
                  : <span className={s.cardTitle}>{primary.cell(row)}</span>}
              </div>
              {rowActions && rowActions(row).length > 0 && <KebabMenu label={rowActionsLabel?.(row)} items={rowActions(row)} />}
            </div>
            <dl className={s.cardBody}>
              {rest.map((c) => (<div key={c.id} className={s.cardRow}><dt>{c.header}</dt><dd>{c.cell(row)}</dd></div>))}
            </dl>
          </li>
        ))}
      </ul>
    );
  }
  return (
    <div className={s.scroll}>
      <table className={s.table}>
        <caption className={VISUALLY_HIDDEN}>{caption}</caption>
        <thead><tr>
          {selection && (
            <th scope="col" className={s.selCell}>
              <Checkbox ref={headRef} label={null} aria-label={allPicked ? selection.noneLabel : selection.allLabel} checked={allPicked} onChange={selection.onToggleAll} />
            </th>
          )}
          {columns.map((c) => <th key={c.id} scope="col">{c.header}</th>)}{rowActions && <th scope="col"><span className={VISUALLY_HIDDEN}>{caption}</span></th>}</tr></thead>
        <tbody>
          {rows.map((row) => (
            <tr key={rowKey(row)} aria-selected={selection ? selection.selected.has(rowKey(row)) : undefined}
              className={[onRowClick ? s.clickable : "", selection?.selected.has(rowKey(row)) ? s.selected : ""].join(" ").trim() || undefined} onClick={onRowClick ? () => onRowClick(row) : undefined}>
              {selection && <td className={s.selCell} onClick={(e) => e.stopPropagation()}>{rowBox(row)}</td>}
              {columns.map((c, i) => (
                <td key={c.id}>{i === 0 && onRowClick ? <button type="button" className={s.rowLink} onClick={(e) => { e.stopPropagation(); onRowClick(row); }}>{c.cell(row)}</button> : c.cell(row)}</td>
              ))}
              {rowActions && <td className={s.actionsCell}>{rowActions(row).length > 0 && <KebabMenu label={rowActionsLabel?.(row)} items={rowActions(row)} />}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
